import React, {useState} from 'react';
import {defineMessages, useIntl} from 'react-intl';
import {defineAccessControlledResource, GroupAccessRule} from '@igp/shared/access-control';
import type {Sorting} from '@igp/shared/api';
import {useAuthUser} from '@igp/shared/auth';
import {ErrorModal} from '@igp/shared/components/error';
import {LayoutQueue, PageHeader, Toolbar, ToolbarItems, ToolbarItemsContainer} from '@igp/shared/components/layout';
import type {UserProfileViewModelKeys} from '@igp/shared/features/user-profile';
import {
    UserProfileDownloadCsvButton,
    UserProfileQueryFilters,
    useUserProfiles,
    useUserProfileSettings,
} from '@igp/shared/features/user-profile';
import {withTraceErrorBoundary} from '@igp/shared/otel';
import {RealtimeMessageTrigger, RealtimeUpdatesMode} from '@igp/shared/redux/realtime';
import type {ViewType} from '@igp/shared/redux/view';
import {getNonEmptyValueValidator} from '@igp/shared/redux/view';
import {EntityType} from '@models';

import {groups, useBoAgentAccessCheck} from '@access-control';
import {agentResources} from '@policy-access';
import {BulkP2PTransferButton, BulkP2PTransferViaFileButton} from '@features/transaction';
import {DataGridUserProfileServer, userProfileServiceTypes} from '@features/user-profile';

const localized = defineMessages({
    title: {
        id: 'PlayerPage_header',
        defaultMessage: 'Players',
    },
});

function PlayerPageInner() {
    const {formatMessage} = useIntl();
    const viewType: ViewType = 'PlayerPage';

    const hasAccessToConditionalColumns = useBoAgentAccessCheck(platformColumnsResource);
    const conditionalColumns: UserProfileViewModelKeys[] = hasAccessToConditionalColumns
        ? ['platform_narrow', 'brand', 'license_type']
        : [];
    const columns: UserProfileViewModelKeys[] = [
        'uid_link_player_details',
        'username',
        'email',
        'register_marketing_code',
        'nickname',
        'country',
        'referrer_player_id',
        'referrer_username',
        'referred_date',
        'finance.balance',
        'finance.withdrawable_balance',
        'finance.casino_coin_balance',
        'deposit_status',
        'withdrawal_status',
        'p2p_status',
        ...conditionalColumns,
        // 'channel', // Commented due to revert of WPT-7888 ticket
    ];

    const defaultSorting: Sorting<UserProfileViewModelKeys>[] = [
        {
            field: 'date_of_joining.seconds',
            sort: 'desc',
        },
    ];
    const boAgentPlayerId = useAuthUser()?.playerId as string;

    const {
        items,
        rawItems,
        totalCount,
        searchFilter,
        filterString,
        handlePageChange,
        handlePageSizeChange,
        handleSortChange,
        handleFilterChange,
    } = useUserProfiles({
        viewType,
        fields: [...columns, 'p2p_override_status'],
        displayName: localized.title,
        defaultPaging: {page: 1, pageSize: 25},
        defaultSorting,
        hiddenFilters: [
            {key: 'isMasterAgentOrHasReferrer', value: 'true'},
            {key: 'defaultReferrerPlayerId', value: boAgentPlayerId},
            {key: 'defaultIsDownstream', value: true},
        ],
        validateFilter: getNonEmptyValueValidator('isMasterAgentOrHasReferrer'),
        syncWithUrl: true,
        currency: 'USD',
        realtimeMode: RealtimeUpdatesMode.Confirm,
        triggers: [{type: RealtimeMessageTrigger.Update}],
    });

    const pinnedColumns: (typeof columns)[number][] = ['uid_link_player_details'];
    const {visibleColumns, onVisibleColumnsChange} = useUserProfileSettings({
        settingId: viewType,
        defaultColumns: columns,
        pinnedColumns,
    });

    const [selectedIds, setSelectedIds] = useState([]);

    const isAgent = useBoAgentAccessCheck(agentResources);

    return (
        <LayoutQueue
            header={
                <PageHeader
                    header={localized.title}
                    actions={
                        <>
                            <BulkP2PTransferButton
                                agentUid={boAgentPlayerId}
                                uids={selectedIds}
                                applyStepGridColumns={[
                                    'uid',
                                    'username',
                                    'p2p_direction',
                                    'finance.balance',
                                    'p2p_transfer_amount_editable',
                                ]}
                            />
                            <BulkP2PTransferViaFileButton
                                agentUid={boAgentPlayerId}
                                applyStepGridColumns={['uid', 'username', 'p2p_direction', 'finance.balance', 'transfer_amount']}
                            />
                            <UserProfileDownloadCsvButton
                                filterString={filterString}
                                filename={formatMessage(localized.title)}
                                keys={visibleColumns}
                                hiddenKeys={['p2p_override_status']}
                                mode="server"
                                downloadServerStrategyId={userProfileServiceTypes.downloadServerStrategy}
                                total={totalCount}
                            />
                        </>
                    }
                />
            }
            toolbar={
                <>
                    <Toolbar>
                        <ToolbarItemsContainer primary>
                            <ToolbarItems streched>
                                <UserProfileQueryFilters
                                    model={filterString}
                                    onChange={handleFilterChange}
                                    availableFilters={[
                                        'uidWithPlaceholder',
                                        'username',
                                        'email',
                                        'marketingCode',
                                        'nickname',
                                        'registrationCountryWithCountryPlaceholder',
                                        'agent',
                                    ]}
                                    countryEntity={EntityType.Country}
                                    agentProfileEntity={EntityType.AgentProfile}
                                    viewType={viewType}
                                    defaultPlayerId={boAgentPlayerId}
                                    isAgent={isAgent}
                                    withResetButton
                                />
                            </ToolbarItems>
                        </ToolbarItemsContainer>
                    </Toolbar>
                </>
            }
            body={
                <DataGridUserProfileServer
                    hasColumnSelection
                    visibleColumns={visibleColumns}
                    pinnedColumns={pinnedColumns}
                    onColumnsVisibilityUpdate={onVisibleColumnsChange}
                    columns={columns}
                    rows={items}
                    rawModels={rawItems}
                    rowCount={totalCount}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    onSortModelChange={handleSortChange}
                    page={searchFilter?.paging?.page}
                    pageSize={searchFilter?.paging?.pageSize}
                    sortModel={searchFilter?.sorting}
                    isFlexible
                    checkboxSelection
                    selectedIds={selectedIds}
                    onSelect={setSelectedIds}
                />
            }
        />
    );
}

const platformColumnsResource = defineAccessControlledResource({
    name: 'PlayersPagePlatformColumnsResource',
    rules: new GroupAccessRule([groups.adminsGroup, groups.agentManagersGroup, groups.countryPartnerGroup]),
});

export const PlayerPage = withTraceErrorBoundary(PlayerPageInner, ErrorModal);
